/**
 * useVideoAnalytics Hook
 * 
 * Integrates with the video player to automatically track and post analytics data to Supabase.
 * This hook handles session management, periodic event posting, and cleanup.
 */

import { useCallback, useMemo, useRef } from "react";
import { getOrCreateJBASessionId } from "../utils/jba";
import { trackVideoCheckpoint } from "../services/videoAnalytics";

type UseVideoAnalyticsConfig = {
  videoId: string;
  checkpointStep?: number;
  enabled?: boolean;
};

const clampPercentage = (value: number) => Math.max(0, Math.min(100, value));

const getCheckpointBucket = (percentage: number, checkpointStep: number) => {
  const normalized = clampPercentage(percentage);
  return Math.min(100, Math.floor(normalized / checkpointStep) * checkpointStep);
};

const getStoredCheckpoints = (storageKey: string) => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    const parsed = raw ? (JSON.parse(raw) as number[]) : [];
    return new Set(parsed.filter((value) => Number.isFinite(value)));
  } catch {
    return new Set<number>();
  }
};

const persistCheckpoints = (storageKey: string, checkpoints: Set<number>) => {
  try {
    sessionStorage.setItem(
      storageKey,
      JSON.stringify(Array.from(checkpoints).sort((a, b) => a - b))
    );
  } catch {
    return;
  }
};

const createTrackingSessionId = () =>
  `${getOrCreateJBASessionId()}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const useVideoAnalytics = ({
  videoId,
  checkpointStep = 10,
  enabled = true,
}: UseVideoAnalyticsConfig) => {
  const sessionIdRef = useRef(createTrackingSessionId());
  const storageKeyRef = useRef(
    `video-retention:${videoId}:${sessionIdRef.current}:${checkpointStep}`
  );
  const sessionId = useMemo(() => sessionIdRef.current, []);
  const storageKey = useMemo(() => storageKeyRef.current, []);
  const trackedCheckpointsRef = useRef<Set<number>>(getStoredCheckpoints(storageKey));

  const trackProgress = useCallback(
    async (percentage: number) => {
      if (!enabled) return;

      const checkpoint = getCheckpointBucket(percentage, checkpointStep);
      const checkpoints = trackedCheckpointsRef.current;
      const currentStorageKey = storageKeyRef.current;
      const currentSessionId = sessionIdRef.current;

      for (let current = 0; current <= checkpoint; current += checkpointStep) {
        if (checkpoints.has(current)) {
          continue;
        }

        checkpoints.add(current);
        persistCheckpoints(currentStorageKey, checkpoints);

        await trackVideoCheckpoint({
          video_id: videoId,
          session_id: currentSessionId,
          checkpoint_percent: current,
        });
      }
    },
    [checkpointStep, enabled, videoId]
  );

  const markCompleted = useCallback(async () => {
    await trackProgress(100);
  }, [trackProgress]);

  const resetSession = useCallback(() => {
    sessionIdRef.current = createTrackingSessionId();
    storageKeyRef.current = `video-retention:${videoId}:${sessionIdRef.current}:${checkpointStep}`;
    trackedCheckpointsRef.current = new Set<number>();
    persistCheckpoints(storageKeyRef.current, trackedCheckpointsRef.current);
  }, [checkpointStep, videoId]);

  return {
    sessionId,
    trackProgress,
    markCompleted,
    resetSession,
  };
};

export default useVideoAnalytics;
